import { createSelector } from '@reduxjs/toolkit';
import LoadingStatus from 'enums/loadingStatus';
import { selectLocation, selectPage } from 'features/searchResultsFacets';
import MarkerType from '../enums/markerType';

/**
 * Returns true if search within map flag is on.
 * @param state - The state of the redux store
 */
export const selectIsSearchWithinMapReady = (state) => state?.mapState?.isSearchWithinMapReady;

/**
 * Returns the geolocation of the point of interest listing.
 * @param state - The state of the redux store
 */
export const selectFocusedGeographicKey = (state) => state?.uiState?.focusedListing?.geographicKey;

/**
 * Returns the Id of the listing being hovered.
 * @param state - The state of the redux store
 */
export const selectFocusedListingId = (state) => state?.uiState?.focusedListing?.id;

/**
 * Returns the community Id of the listing being hovered.
 * @param state - The state of the redux store
 */
export const selectFocusedCommunityId = (state) => state?.uiState?.focusedListing?.communityId;

/**
 * Returns true if the Geo Coordinates object has been defined.
 * @param state - The state of the redux store
 */
export const areMapBoundariesDefined = (state) => !!state?.mapState?.geoCoordinates;

/**
 * If the state has a mapState property, and that property has a HomeResults property, then return true.
 * Otherwise, return false
 * @param state - The state of the Redux store.
 */
export const areMarkersAvailable = (state) => !!state?.mapState?.results;

/**
 * It returns the Geo Coordinates from the mapState in the Redux store.
 * @param state - The state of the redux store
 */
export const geoCoordinates = (state) => state?.mapState?.geoCoordinates;

/**
 * Returns true if the map is not fetching results from the server.
 * @param state - The state of the Redux store.
 */
export const isMapIdle = (state) => !state?.mapState?.isFetchingResults;

/**
 * Returns true if the map is fetching results from the server.
 * @param state - The state of the Redux store.
 */
export const isFetchingResults = (state) => state?.mapState?.isFetchingResults;

/**
 * Returns the map library status.
 * @param state - The state of the Redux store.
 */
export const selectMapLibraryStatus = (state) => state?.mapState?.mapLibraryStatus;

/**
 * Returns true if the map library is fetching.
 * @param state - The state of the Redux store.
 */
export const isMapLibraryFetching = (state) => state?.mapState?.mapLibraryStatus === LoadingStatus.Loading.toString();

/**
 * Returns true if the map library has been loaded.
 * @param state - The state of the Redux store.
 */
export const isMapLibraryLoaded = (state) => state?.mapState?.mapLibraryStatus === LoadingStatus.Succeeded.toString();

/**
 * If the state has a mapState property,and the property has a isSearchWithinMap property, return that property.
 *
 * @param state - The state of the Redux store.
 */
export const isSearchWithinMapEnabled = (state) => state?.mapState?.isSearchWithinMap;

/**
 * It returns the results from the mapState in the Redux store.
 * @param state - The state of the redux store
 */
export const mapMarkers = (state) => state?.mapState?.results;

/**
 * Returns an array representing all of the current plan identifiers.
 * @param state - The state of the redux store
 */
export const selectAllPlansIds = (state) => state?.mapState?.allPlanIds;

/**
 * If the user click a marker, it is store in this property.
 */
export const selectedMarker = (state) => state?.mapState?.selectedMarker;

/**
 * the summary cards information.
 */
export const selectSummaryCards = (state) => state?.mapState?.summaryCards;

/**
 * the summary card status information.
 */
export const selectSummaryCardsStatus = (state) => state?.mapState?.summaryCardsStatus;

/**
 * the current map zoom level.
 */
export const selectZoomLevel = (state) => state?.mapState?.zoomLevel;

/**
 * selects community site plans information.
 */
export const selectSitePlans = (state) => state?.mapState?.sitePlans;

/**
 * selects interactive site plan information.
 */
export const selectInteractiveSitePlans = (state) => state?.interactiveSitePlanState?.interactiveSitePlans;

/**
 * selects interactive site plan tracking information to understand which one are loaded.
 */
export const selectTrackedInteractiveSitePlans = (state) =>
  state?.interactiveSitePlanState?.loadedInteractiveSitePlans;

/**
 * selects the lot number clicked by the user
 */
export const selectLotNumber = (state) => state?.interactiveSitePlanState?.lotNumber;

/**
 * selects the lot identifier
 */
export const selectLotId = (state) => state?.interactiveSitePlanState?.lotId;

/**
 * selects the community id of the current active lot
 */
export const selectLotCommunityId = (state) => state?.interactiveSitePlanState?.communityId;

/**
 * select the available plans that can be built in a selected lot
 */
export const selectAvailablePlans = (state) => state?.interactiveSitePlanState?.availablePlans;

/**
 * select the spec built in a selected lot
 */
export const selectAvailableSpec = (state) => state?.interactiveSitePlanState?.availableSpec;

/**
 * select the active lot status of an interactive site plan
 */
export const selectLotStatus = (state) => state?.interactiveSitePlanState?.lotStatus;

/**
 * selects information about lot summary card.
 */
export const selectedLotSummaryCard = (state) =>
  state?.interactiveSitePlanState?.interactiveSitePlanCard;

/**
 * selects information about active lot, specifically: site plan id.
 */
export const selectedLotSitePlanId = (state) =>
  state?.interactiveSitePlanState?.sitePlanId;

/**
 * Gets the data for the summary card, and the marker that triggered
 */
export const selectSummaryCardsStatusAndMarkerData = createSelector(
  selectSummaryCardsStatus,
  selectedMarker,
  (status, marker) => ({ status, marker })
);

/**
 * Gets the data for the summary card, including the card data and the card status
 */
export const selectSummaryCardsInformation = createSelector(
  selectSummaryCardsStatus,
  selectSummaryCards,
  (status, cards) => ({ status, cards })
);

/**
 * Gets data related to lot information.
 */
export const selectLotInformation = createSelector(
  selectLotId,
  selectLotCommunityId,
  selectedLotSummaryCard,
  selectedLotSitePlanId,
  (
    lotId,
    communityId,
    summaryCard,
    sitePlanId) => ({
      lotId,
      communityId,
      summaryCard,
      sitePlanId
    })
);

/**
 * Gets the community id of the opened lot summary card along with the site plans.
 */
export const selectLotCommunityIdAndSitePlans = createSelector(
  selectLotCommunityId,
  selectSitePlans,
  (
    lotCommunityId,
    sitePlans) => ({
      lotCommunityId,
      sitePlans,
    })
);

/**
 * It returns the point of interest hovered by the user
 */
export const selectFocusedListing = createSelector(
  selectFocusedListingId,
  selectFocusedCommunityId,
  selectFocusedGeographicKey,
  (id, communityId, geographicKey) => ({
    id,
    communityId,
    geographicKey,
  })
);

/**
 * It returns the current map geo coordinates along with the current location.
 */
export const selectGeoCoordinatesAndLocation = createSelector(
  geoCoordinates,
  selectLocation,
  (coordinates, location) => ({
    coordinates,
    location,
  })
);

export const selectSearchMapTrackingCommonProperties = createSelector(
  isSearchWithinMapEnabled,
  (isSearchWithinMap) => ({ isSearchWithinMap })
);

export const selectedMarkerAndPage = createSelector(
  selectedMarker,
  selectPage,
  (marker, page) => (
    {
      marker, 
      page
    }
  )
);

/**
 * selector that will retrieve information related with current map zoom level, map geo coordinates,
 * search within map status and map markers.
 */
export const selectSitePlansProperties = createSelector(
  selectZoomLevel,
  geoCoordinates,
  isSearchWithinMapEnabled,
  mapMarkers,
  isFetchingResults,
  isMapLibraryLoaded,
  selectSitePlans,
  (zoomLevel, coordinates, isSearchWithinMap, markers, isFetching, isMapLoaded, sitePlans) => (
    {
      zoomLevel,
      coordinates,
      isSearchWithinMap,
      markers,
      isFetching,
      isMapLoaded,
      sitePlans,
    }
  )
);

/**
 * selector that will retrieve information related with map markers and community site plans.
 */
export const selectMarkersAndSitePlans = createSelector(
  mapMarkers,
  selectSitePlans,
  (markers, sitePlans) => (
    {
      markers,
      sitePlans,
    }
  )
);

/**
 * selector that will retrieve information related with the focused listing card and community site plans.
 */
export const selectFocusedListingAndSitePlans = createSelector(
  selectFocusedListing,
  selectSitePlans,
  (focusedListing, sitePlans) => (
    {
      focusedListing,
      sitePlans,
    }
  )
);

/**
 * selector that will retrieve all specs and plans represented in the current results page.
 */
export const selectAllPlansAndSpecsIds = createSelector(
  mapMarkers,
  selectAllPlansIds,
  (markers, allPlans) => ({
    specs: markers?.filter((marker) => marker.type === MarkerType.SpecUnit.toString()).map((spec) => spec.id) || [],
    plans: allPlans || [],
  })
);

